/**
 * Coordinate planes: geometry, random points and typed ordered pairs.
 */

import { type Rng, randInt } from "@/lib/tool-kit/worksheets/random";

export type Quadrants = "one" | "four";

export interface PlaneSpec {
  quadrants: Quadrants;
  /** The axes run to this value (in one quadrant 0 to max, in four −max to max). */
  max: number;
}

export interface Point {
  x: number;
  y: number;
}

export const PLANE_MAXIMA = [5, 6, 8, 10, 12, 15, 20] as const;
const DECIMAL_PAIR_NUMBER = /^-?(?:\d+\.?\d*|\.\d+)$/;
const PARENTHESISED = /\(([^()]*)\)/g;
const ITEM_BREAK = /[;\n]+/;
const COMPONENT_BREAK = /[,;\s]+/;
const MAX_FRACTION_DIGITS = 2;

export function planeRange(spec: PlaneSpec) {
  const low = spec.quadrants === "one" ? 0 : -spec.max;
  return { minX: low, maxX: spec.max, minY: low, maxY: spec.max };
}

export interface PlaneGeometry {
  /** Pixels per unit. */
  cell: number;
  /** x value to pixel. */
  px: (x: number) => number;
  /** y value to pixel (larger y is higher on the page). */
  py: (y: number) => number;
}

const round = (value: number) => Math.round(value * 100) / 100;

/** Maps plane values onto a `size` × `size` drawing with `pad` of room on every side. */
export function planeGeometry(
  spec: PlaneSpec,
  size: number,
  pad: number
): PlaneGeometry {
  const range = planeRange(spec);
  const units = range.maxX - range.minX;
  const cell = (size - 2 * pad) / units;
  return {
    cell: round(cell),
    px: (x) => round(pad + (x - range.minX) * cell),
    py: (y) => round(pad + (range.maxY - y) * cell),
  };
}

/** A label step that keeps axis numbers from crowding: 1, 2 or 5 by cell size. */
export function autoLabelEvery(cell: number): number {
  if (cell >= 24) {
    return 1;
  }
  return cell >= 12 ? 2 : 5;
}

/** 1 to 4 for the quadrants (I upper right, counter-clockwise), 0 on an axis. */
export function quadrantOf(p: Point): 0 | 1 | 2 | 3 | 4 {
  if (p.x === 0 || p.y === 0) {
    return 0;
  }
  if (p.x > 0) {
    return p.y > 0 ? 1 : 4;
  }
  return p.y > 0 ? 2 : 3;
}

/** `(−4, 1)`: a point written as an ordered pair with true minus signs. */
export function formatPair(p: Point): string {
  const part = (value: number) =>
    value < 0 ? `−${Math.abs(value)}` : String(value);
  return `(${part(p.x)}, ${part(p.y)})`;
}

/* ----------------------------------------------------------------- random points */

export interface PointOptions {
  count: number;
  /** Let coordinates be 0, putting points on an axis. */
  allowAxes: boolean;
  /** In four quadrants, take points from each quadrant in turn. */
  spread: boolean;
}

const QUADRANT_SIGNS = [
  [1, 1],
  [-1, 1],
  [-1, -1],
  [1, -1],
] as const;

function coordinate(rng: Rng, max: number, allowAxes: boolean): number {
  return randInt(rng, allowAxes ? 0 : 1, max);
}

/** Distinct whole-number points inside the plane. */
export function generatePoints(
  spec: PlaneSpec,
  options: PointOptions,
  rng: Rng
): Point[] {
  const points: Point[] = [];
  const seen = new Set<string>();
  const four = spec.quadrants === "four";
  const capacity = four
    ? (2 * spec.max + (options.allowAxes ? 1 : 0)) ** 2
    : (spec.max + (options.allowAxes ? 1 : 0)) ** 2;
  const wanted = Math.min(options.count, capacity);
  for (
    let attempt = 0;
    points.length < wanted && attempt < wanted * 80;
    attempt += 1
  ) {
    const signs =
      four && options.spread
        ? QUADRANT_SIGNS[points.length % 4]
        : QUADRANT_SIGNS[randInt(rng, 0, 3)];
    const [sx, sy] = four ? (signs ?? [1, 1]) : [1, 1];
    const point: Point = {
      x: sx * coordinate(rng, spec.max, options.allowAxes),
      y: sy * coordinate(rng, spec.max, options.allowAxes),
    };
    const key = `${point.x},${point.y}`;
    if (!seen.has(key)) {
      seen.add(key);
      points.push({ x: point.x + 0, y: point.y + 0 });
    }
  }
  return points;
}

/* ------------------------------------------------------------------ typed points */

function readNumber(raw: string): number | null {
  const text = raw.trim().replace("−", "-");
  if (!DECIMAL_PAIR_NUMBER.test(text)) {
    return null;
  }
  const value = Number(text);
  const places = (text.split(".")[1] ?? "").length;
  return places > MAX_FRACTION_DIGITS ? null : value;
}

function readPair(chunk: string): Point | null {
  const parts = chunk
    .replaceAll("(", "")
    .replaceAll(")", "")
    .trim()
    .split(COMPONENT_BREAK)
    .filter((part) => part !== "");
  if (parts.length !== 2) {
    return null;
  }
  const x = readNumber(parts[0] ?? "");
  const y = readNumber(parts[1] ?? "");
  return x === null || y === null ? null : { x: x + 0, y: y + 0 };
}

function onPlane(spec: PlaneSpec, p: Point): boolean {
  const range = planeRange(spec);
  return (
    p.x >= range.minX &&
    p.x <= range.maxX &&
    p.y >= range.minY &&
    p.y <= range.maxY
  );
}

/**
 * Ordered pairs typed as `(2, 3) (−4, 1)` or one per line / per `;` as `2, 3`. Decimals
 * use a point (the comma separates the two numbers). Pairs outside the plane, or that
 * cannot be read, come back in `rejected`.
 */
export function parsePairs(
  text: string,
  spec: PlaneSpec
): { items: Point[]; rejected: string[] } {
  const chunks = [...text.matchAll(PARENTHESISED)].map((m) => m[1] ?? "");
  const entries =
    chunks.length > 0
      ? chunks
      : text.split(ITEM_BREAK).map((piece) => piece.trim());
  const items: Point[] = [];
  const rejected: string[] = [];
  for (const entry of entries) {
    if (entry.trim() === "") {
      continue;
    }
    const pair = readPair(entry);
    if (pair && onPlane(spec, pair)) {
      items.push(pair);
    } else {
      rejected.push(entry.trim());
    }
  }
  return { items, rejected };
}
